/**
 * THE DOOR, at `/`. The one screen a person reaches holding nothing, so everything on it works
 * before anybody is known: what this place is, how to get in, and a phone number that is always a
 * way forward.
 *
 * Phone first, in the website's tokens, with the header and the buttons taken from
 * `faithfully-kept-business/design/home/NoMembership.dc.html`: a white header on a hairline, the mark
 * beside the name, and a 56px navy Call button. The words are the brief's, exactly. At a desk the
 * page is the one centred column every page is drawn in (`components/column.ts`), so no button or
 * line runs the width of the window. The call button is outlined beside Sign in, as the design
 * draws Email us beside Call, and navy when it is the one way forward, on the closed answer; while
 * the question is out it stays outlined, so the usual answer changes nothing.
 *
 * WHETHER THE SIGN IN BUTTON IS DRAWN IS THE SERVER'S ANSWER, NOT THIS FILE'S. /api/health says
 * `signIn: true` only once a code can be sent by email. Until then, and whenever the question cannot
 * be answered, the screen says sign-in is not open and draws no button, because a button that leads
 * to a refusal is a dead end on the one screen with nothing else on it. When it is open, the button
 * leads to `/sign-in`, the two steps in this screen's style: the email address, then the code.
 *
 * While the question is out the space its answer takes is held open, so the call button below does
 * not jump under a thumb when the answer arrives.
 *
 * HOW THE CODE COMES IS THE SAME ANSWER (home #11): where /api/health says `signInByText`, which the
 * sign-in steps read to take a mobile number, the door says the code comes by email or text; until
 * it does, and wherever it does not, it says the brief's words, that it comes by email.
 *
 * Every piece of text is at the body size, 19px on a phone, except the eyebrow and the hours under
 * the call button, which are the website's small step as the design draws them. Every link is a
 * box at least 48px tall.
 *
 * DRAWN IN PLACE OF A SIGNED-IN SCREEN, after a sign-out or a 401 in the middle of a session, the
 * door is where she was, with no move between places, so the focus goes to its heading as after a
 * move (`components/RouteFocus.tsx`). On a first load it stays where a page starts.
 *
 * DRAWN IN PLACE OF THE PAGE A LINK LED HER TO (the owner's decision of 2026-10-19, ops #76,
 * `docs/seam.md` P107): at a deeper page, an invoice's own say, signed out, its Sign in carries
 * that page, its path and its query, to the sign-in steps (`lib/cameFor.ts`, `signInFor`), which
 * bring her back to it once her code has gone in. At Home, and anywhere that is not one of home's
 * places, it carries nothing, and the address stays as it was either way.
 *
 * THE WORDS BACK FROM STRIPE'S PAGE, ABOVE THE SIGN-IN (ops's plan for clients reaching only home,
 * section 7, ask 3). A payer through the office's page, or one home asked for, may come back with no
 * session: she may hold no door, or not sign in. So drawn at an invoice's own page or at the page
 * Stripe sends her back to, with an address back the contract defines (`page=`, `saves=`,
 * `not-paid`; `returnAt`, `lib/paying.ts`), the door first says section 5's words for it, "Payments"
 * over "Thank you." or "You have not paid yet." and the callout, word for word as the return page
 * says them (`components/ReturnWords.tsx`), with nothing of hers: no figure, no number, no name. With
 * `not-paid` the line on how to pay is section 5's own, since no invoice and no Pay are below it
 * here. Their heading is then the page's `h1`, and the door's own heading an `h2` under it, so a
 * screen reader hears what came of her payment first. Everything else is as it is without them: the
 * sign-in carries an invoice's page, query and all, and the page Stripe sends her back to not at
 * all, as ever (P107). With no such address, the door is as it always was.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getHealth } from '../api.ts';
import { GHOST_BUTTON, NAVY_BUTTON } from '../components/buttons.ts';
import { COLUMN } from '../components/column.ts';
import { Footer } from '../components/Footer.tsx';
import { Header } from '../components/Header.tsx';
import { ReturnWords } from '../components/ReturnWords.tsx';
import { useHeadingFocus } from '../components/RouteFocus.tsx';
import { HOURS, PHONE_HREF, PHONE_SHOWN } from '../contact.ts';
import { cameForHere, signInFor } from '../lib/cameFor.ts';
import { returnAt } from '../lib/paying.ts';

type SignIn = 'asking' | 'open' | 'closed';

interface DoorProps {
  /** Whether the door is drawn in place of a signed-in screen she was on, with no move. */
  readonly inPlace?: boolean;
}

export function Door({ inPlace = false }: DoorProps) {
  useHeadingFocus(inPlace);
  const [signIn, setSignIn] = useState<SignIn>('asking');
  /** Whether this host can text a code too, as the sign-in steps read it. */
  const [byText, setByText] = useState(false);
  const location = useLocation();
  /** The page a link led her to, where the door is drawn in its place, for the sign-in to keep. */
  const cameFor = cameForHere(location);
  /** What the address back from Stripe's page says, where one returned her (ask 3), or none. */
  const back = returnAt(location);
  /** The door's own heading, the page's heading unless the words back are said above it. */
  const Heading = back === null ? 'h1' : 'h2';

  useEffect(() => {
    let live = true;
    void getHealth().then((health) => {
      if (!live) return;
      setSignIn(health.signIn ? 'open' : 'closed');
      setByText(health.signInByText);
    });
    return () => {
      live = false;
    };
  }, []);

  return (
    <div className="flex min-h-dvh flex-col bg-surface-page">
      <Header />

      <main className={`${COLUMN} flex-1 px-5 py-8`}>
        {back === null ? null : (
          <div data-returned className="mb-8 flex flex-col gap-5">
            <ReturnWords back={back} below="nothing" />
          </div>
        )}
        <p className="font-mono text-small tracking-widest text-text-emphasis uppercase">
          For clients
        </p>
        <Heading className="mt-2 font-serif text-h2 font-semibold text-pretty text-text-body">
          Sign in to see your home
        </Heading>
        <p className="mt-4 text-body">
          {byText
            ? 'We email or text you a code to sign in. There is no password.'
            : 'We email you a code to sign in. There is no password.'}
        </p>
        <p className="mt-3 text-body">A home appears here once we invite you to it.</p>

        <div className="mt-7 flex min-h-tap-call flex-col justify-center" aria-live="polite">
          {signIn === 'open' ? (
            <Link to={signInFor(cameFor)} className={NAVY_BUTTON}>
              Sign in
            </Link>
          ) : signIn === 'closed' ? (
            <p className="text-body text-text-muted">Sign-in is not open yet.</p>
          ) : null}
        </div>

        <a href={PHONE_HREF} className={`mt-3 ${signIn === 'closed' ? NAVY_BUTTON : GHOST_BUTTON}`}>
          {`Call ${PHONE_SHOWN}`}
        </a>
        <p className="mt-4 text-small text-text-muted">{HOURS}</p>
      </main>

      <Footer />
    </div>
  );
}
